// Copyright (c) StrangeBee 2024

import React, { ReactElement, useCallback, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ActionListRendererParams, trimTags, useLocalMessage } from '@Helpers/';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import RouteBuilder from '@Services/RouteBuilder';
import {
    CaseOutput,
    CustomStatusMapType,
    SharedOrgOutput,
    UserOutput,
    CaseStatusStage,
    PermissionsEnum,
} from 'thehive-sdk';
import { CaseEventList } from '@Organisms/Case/eventList';
import { THForm } from '@Molecules/THForm/THForm';
import { Form, Space } from 'antd';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import Details from '@Molecules/Details/Details';
import { extractData } from '@Organisms/Case/CaseDrawer/extractData';
import useListQuery from '@Hooks/useListQuery';
import client from '@Client';
import { FormatDate } from '@Molecules/Format';
import AssigneeInfo from '@Molecules/AssigneeInfo/AssigneeInfo';
import { DisplayMetadata } from '@Molecules/DisplayMetadata/DisplayMetadata';
import { CopyValue } from '@Atoms/CopyValue/CopyValue';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { ICONS } from '@Constants/iconsApp';
import { useListAllFields } from '@Queries/describeQueries';
import { ScopeContext } from '@Context/ScopeProvider';
import { mergeCF } from '@Helpers/customField';
import { mergeTag } from '@Helpers/array';
import { useSplitPermissions } from '@Hooks/useSplitPermissions';

import './CaseDrawer.scss';

interface CaseDrawerProps {
    data: CaseOutput;
    eventList?: CaseEventList;
    triggerElem?: ReactElement;
    listShared?: SharedOrgOutput[];
    actionList?: ActionListRendererParams<CaseOutput>;
    defaultVisible?: boolean;
    onVisibleChange?: (visible: boolean) => void;
    visible?: boolean;
    hasPermissionsDrawer?: boolean;
    afterVisible?: (visible: boolean) => void;
    onEditCounter?: () => void;
}

export const CaseDrawer = ({
    data,
    eventList,
    triggerElem,
    actionList,
    defaultVisible = false,
    onVisibleChange,
    hasPermissionsDrawer = true,
    visible,
    afterVisible,
    onEditCounter,
}: CaseDrawerProps): ReactElement => {
    const i = useLocalMessage();
    const { data: describeData } = useListAllFields();
    const [form] = Form.useForm();

    const [currentData, setCurrentData] = React.useState<CaseOutput>(data);
    const [updatedData, setUpdatedData] = React.useState<boolean>(false);
    const [forceUpdate, setForceUpdate] = React.useState<boolean>(false);
    const [activeKeyCF, setActiveKeyCF] = useState('0');

    const {
        _id: caseId,
        number,
        severity = 1,
        tlp,
        pap,
        extraData,
        _createdBy,
        _createdAt,
        _updatedAt,
        stage,
        impactStatus,
    } = currentData;

    const {
        contributors = [],
        taskStats,
        observableStats = { total: 0 },
        procedureCount,
        permissions = [],
    } = extraData || {};

    const [fieldsTouched, setFieldsTouched] = useState(false);
    const [caseStatus, setCaseStatus] = React.useState<CustomStatusMapType>({});

    const [editMode, setEditMode] = React.useState(false);

    const hasPermissions = useSplitPermissions(permissions, [PermissionsEnum.MANAGE_CASE_UPDATE], hasPermissionsDrawer);

    const { data: assignableUsers = [], refetch: refetchAssignableUsers } = useListQuery<UserOutput[]>({
        queryParams: {
            selector: {
                head: [{ name: 'getCase', idOrName: caseId }, { name: 'assignableUsers' }],
                name: `get-case-${caseId}-assignable-users`,
                initialFilters: [
                    {
                        field: 'locked',
                        operator: 'any',
                        values: [false],
                        type: 'boolean',
                        describeAttribute: describeData?.user.attributes.find(({ name }) => name === 'locked') as any,
                    },
                ],
                sort: ['+name'],
            },
        },
        controller: client.UserController,
        manual: true,
        fetchOnStart: false,
    });

    const {
        customStatus: {
            case: { list: customStatusList },
        },
    } = useContext(ScopeContext);

    const { customFieldList: customFieldList } = useContext(ScopeContext);

    const isOpen = stage !== CaseStatusStage.CLOSED;

    const editCase = useCallback(
        ({ customFields, tags, ...values }) => {
            const treatedCF = customFields
                ? mergeCF(currentData?.customFields || [], data?.customFields || [], customFields)
                : [];

            const treatedTag = tags ? mergeTag(currentData?.tags || [], data?.tags || [], tags) : [];
            if (eventList) {
                eventList
                    ?.onEdit(
                        {
                            ...values,
                            ...(customFields ? { customFields: treatedCF } : {}),
                            ...(tags ? { tags: trimTags(treatedTag) } : {}),
                            assignee: 'assignee' in values && !values.assignee ? null : values.assignee,
                        },
                        caseId,
                    )
                    .finally(() => {
                        setForceUpdate(true);
                        setFieldsTouched(false);
                        setEditMode(false);
                    });
            } else {
                setForceUpdate(true);
                setFieldsTouched(false);
                setEditMode(false);
            }
        },
        [data, caseId, currentData, eventList],
    );

    useEffect(() => {
        form.setFieldsValue(currentData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentData]);

    useEffect(() => {
        if (!editMode && updatedData) {
            setCurrentData(data);
            setUpdatedData(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editMode, updatedData]);

    useEffect(() => {
        if ((!editMode && data) || forceUpdate) {
            setCurrentData(data);
            setForceUpdate(false);
        } else {
            setUpdatedData(true);
        }
    }, [data, editMode, forceUpdate, form]); // eslint-disable-line

    useEffect(() => {
        if (customStatusList.length) {
            const caseStatusMap = customStatusList.reduce((acc, curr) => {
                if (
                    isOpen &&
                    ![CaseStatusStage.NEW, CaseStatusStage.IN_PROGRESS].includes(curr.stage as CaseStatusStage)
                ) {
                    return acc;
                }
                if (!acc[curr.stage]) {
                    acc[curr.stage] = [];
                }
                acc[curr.stage].push(curr);
                return acc;
            }, {} as CustomStatusMapType);
            setCaseStatus(caseStatusMap);
        }
    }, [customStatusList, isOpen]);

    return (
        <THDrawer
            title={
                <Space>
                    <ICONS.CaseIcon />
                    {`${i('common.case')} #${number}`}
                </Space>
            }
            defaultVisible={defaultVisible}
            triggerElem={triggerElem}
            className="drawer-form"
            width="55vw"
            visible={visible}
            disableClose={fieldsTouched}
            afterVisibleChange={(visible) => {
                form.resetFields();
                if (visible) {
                    refetchAssignableUsers();
                }
                onVisibleChange?.(visible);
                afterVisible?.(visible);
            }}
        >
            <THForm<CaseOutput>
                name="case-drawer-form"
                form={form}
                editable
                initialValues={{
                    ...currentData,
                    customFields: currentData?.customFields?.sort(({ value: valueA }, { value: valueB }) =>
                        `${valueA}`.localeCompare(`${valueB}`),
                    ),
                }}
                onValuesChange={() => {
                    setFieldsTouched(true);
                    setEditMode(true);
                }}
                //eslint-disable-next-line
                onFinish={editCase}
                submitTouchedValues={['customFields']}
            >
                <THForm.Layout
                    header={
                        <DisplayMetadata
                            items={[
                                {
                                    icon: <ICONS.SettingIcon />,
                                    label: 'id',
                                    value: <CopyValue value={caseId}>{caseId}</CopyValue>,
                                },
                                {
                                    icon: <ICONS.UserIcon />,
                                    label: i('common.created-by'),
                                    value: <AssigneeInfo assignee={_createdBy} noAvatar />,
                                },
                                {
                                    icon: <ICONS.CreationDateIcon />,
                                    label: i('common.created-at'),
                                    value: <FormatDate date={_createdAt} />,
                                },
                                {
                                    icon: <ICONS.UpdateDateIcon />,
                                    label: i('common.updated-at'),
                                    value: <FormatDate date={_updatedAt} />,
                                    hidden: !_updatedAt,
                                },
                            ]}
                        />
                    }
                    footer={
                        editMode ? (
                            <FormSubmitButton
                                onCancel={() => {
                                    form.resetFields();
                                    setFieldsTouched(false);
                                    setForceUpdate(true);
                                    setEditMode(false);
                                }}
                                disabled={false}
                                footerText={i('general.confirm')}
                                hidden={!editMode}
                                formName="case-drawer-form"
                            />
                        ) : (
                            <THDrawer.Footer
                                actions={
                                    hasPermissions &&
                                    actionList && (
                                        <ActionsList
                                            actionList={actionList(
                                                [data],
                                                () => {
                                                    //empty
                                                },
                                                'details',
                                            )}
                                            scope="single"
                                        />
                                    )
                                }
                            >
                                <Link to={RouteBuilder.compute('cases.details', { caseId: data?._id || '-' })}>
                                    <THButton type="primary">{i('common.goToDetails')}</THButton>
                                </Link>
                            </THDrawer.Footer>
                        )
                    }
                >
                    <Details
                        data={{
                            ...data,
                            '#computeMetadata': {},
                            '#computeSecurity': {
                                tlp,
                                pap,
                                severity,
                            },
                            '#computeStatus': {
                                statusList: caseStatus,
                                isOpen,
                                impactStatus,
                            },
                            '#computeAssigneeContributors': {
                                assignableUsers,
                                refetchAssignableUsers,
                                contributors,
                            },
                            '#computeCount': {
                                taskStats,
                                observableStats,
                                procedureCount,
                                permissions,
                                hasPermissions,
                                onEditCounter,
                            },
                            '#computeCustomFields': {
                                customFields: customFieldList,
                                form,
                                activeKeyState: {
                                    activeKey: activeKeyCF,
                                    setActiveKey: setActiveKeyCF,
                                },
                            },
                        }}
                        extract={extractData({
                            locale: i,
                            data,
                            hasPermissions,
                            formEditMode: editMode,
                        })}
                    />
                </THForm.Layout>
            </THForm>
        </THDrawer>
    );
};
